/**
 * Christmas avatars — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasAvatarsPack: ToilePack = {
    slug: 'christmas-avatars',
    category: 'season',
    name: 'Christmas avatars',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'reindeer', label: 'Reindeer avatar', subject: 'a reindeer face with a red nose and antlers set inside a round frame, built to read small'},
        {key: 'snowman', label: 'Snowman avatar', subject: 'a snowman face with a carrot nose and a top hat set inside a round frame, built to read small'},
        {key: 'gingerbread', label: 'Gingerbread avatar', subject: 'a gingerbread man face with icing set inside a round frame, built to read small'},
    ],
    styles: [
        {key: 'scandi', label: 'Scandi folk', style: 'scandinavian folk illustration, simplified shapes, no outlines, matte flat colour', palette: 'A limited palette of red, white, forest green and a touch of gold.', framing: 'Centered on a solid cream background, built to read clean at small size.'},
        {key: 'vintage', label: 'Vintage card', style: 'mid-century christmas card illustration, soft halftone grain, slight misregistration', palette: 'Muted red, pine green, cream and a faded gold.', framing: 'Centered on an aged cream paper background with an even margin.'},
        {key: 'cozy', label: 'Cozy cartoon', style: 'cozy cartoon illustration, rounded shapes, thick soft outlines, warm and friendly', palette: 'Warm reds and greens with a glowing amber and snow white.', framing: 'Centered on a solid background with a little room around it.'},
    ],
    directions: ['an elf instead', 'for a gaming profile', 'as a square frame instead'],
    directionHint: 'try: an elf instead',
    seo: {
        title: 'Christmas Avatars: Reindeer, Snowman, Gingerbread',
        metaDescription: 'Nine christmas avatars -- reindeer, snowman, gingerbread -- in scandi folk, vintage card, and cozy cartoon finishes. Round, built to read small.',
        h1: 'Christmas profile picture: reindeer, snowman, and gingerbread avatars',
        tagline: 'Nine faces, three December characters, three finishes each.',
        intro: 'Nine round christmas avatar designs: a reindeer face, a snowman face, and a gingerbread man face, each built to read small inside a round frame. Three finishes -- scandi folk, vintage card, and cozy cartoon -- for a profile picture on any platform. Ask for an elf instead, or a square frame, and the same three finishes carry it.',
        useCases: [
            'a social media profile picture',
            'a Discord or gaming avatar',
            'a group chat icon set',
            'a holiday email signature icon',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine round avatar designs -- a reindeer, a snowman, and a gingerbread man -- each in scandi folk, vintage card, and cozy cartoon finishes, built to read clean at small size.'},
            {q: 'Does this come as a circle already?', a: 'Yes -- each avatar is composed inside a round frame, ready to drop straight into a profile picture slot.'},
            {q: 'Can I get a different character, like an elf?', a: 'Yes -- the direction field takes any variation, including an elf instead, a square frame, or a gaming-profile crop.'},
            {q: 'Can I use this for a business account?', a: 'Yes -- the avatars are yours to use for personal or business profiles across any platform.'},
        ],
    },
}
